import React, { useRef, useEffect, useState } from "react";
import { Task } from "../../types/public-types";
import { BarTask } from "../../types/bar-task";
import styles from "./tooltip.module.css";
import dateFormater from "@/helperFuntions/dateFormater";
import { UserType } from "@/api/query/useCurrentUserQuery";
import UserAvatar from "@/components/ui/userAvatar";
import { t } from "i18next";

export type TooltipProps = {
  task: BarTask;
  arrowIndent: number;
  rtl: boolean;
  svgContainerHeight: number;
  svgContainerWidth: number;
  svgWidth: number;
  headerHeight: number;
  taskListWidth: number;
  rowHeight: number;
  fontSize: string;
  fontFamily: string;
  wrapperRef: React.RefObject<HTMLDivElement>;
  userId?: string;
  TooltipContent: React.FC<{
    task: Task;
    fontSize: string;
    fontFamily: string;
    userId?: string;
  }>;
};

export const Tooltip: React.FC<TooltipProps> = ({
  task,
  userId,
  fontSize,
  fontFamily,
  TooltipContent,
  wrapperRef,
}) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [mouseX, setMouseX] = useState(task.x1);
  const [mouseY, setMouseY] = useState(task.y);

  const updateTooltipPosition = (event: MouseEvent) => {
    if (!wrapperRef.current || !tooltipRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let newMouseX = event.clientX - wrapperRect.left;
    let newMouseY = event.clientY - wrapperRect.top;

    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;
    const maxX = wrapperRect.width - tooltipWidth;
    const maxY = wrapperRect.height - tooltipHeight;

    if (newMouseX > maxX) newMouseX = maxX;
    if (newMouseY > maxY) newMouseY = maxY;

    setMouseX(Math.max(0, newMouseX));
    setMouseY(Math.max(0, newMouseY));
  };

  useEffect(() => {
    const delayedSetup = () => {
      window.addEventListener("mousemove", updateTooltipPosition);
    };

    const timer = setTimeout(() => {
      delayedSetup();
    }, 50); // Delay ensures DOM is rendered

    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", updateTooltipPosition);
    };
  }, [tooltipRef.current]);

  const tooltipStyle = {
    left: `${mouseX + 10}px`,
    top: `${mouseY -10}px`,
    position: "absolute" as const,
    zIndex: 999,
  };

  return (
    <div
    ref={tooltipRef}
    style={tooltipStyle}
    className={`max-w-[250px] bg-white shadow-xl rounded p-2 px-3 ${styles.tooltipDetailsContainer}`}
  >
    <TooltipContent
      task={task}
      fontSize={fontSize}
      fontFamily={fontFamily}
      userId={userId}
    />
  </div>
  );
};

// --- Optional: Tooltip content components ---

export const UserTooltipContent: React.FC<{
  task: any;
  fontSize: string;
  fontFamily: string;
  userId?: string;
}> = ({ task, userId }) => {
  const user = task.assign.find(
    (user: { user: UserType }) => user.user.userId === userId
  )?.user;
  return (
    <div className="relative flex gap-2 items-center ">
      {user && <UserAvatar user={user} fontClass="!text-sm" />}
      <div>
        <h3 className="text-sm  ">
          {user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : user?.email}
        </h3>
        {user?.firstName && user?.lastName && (
          <h3 className="text-sm">{user?.email}</h3>
        )}
      </div>
    </div>
  );
};
export const StandardTooltipContent: React.FC<{
  task: any;
  fontSize: string;
  fontFamily: string;
}> = ({ task }) => {
  return (
    <div className="relative ">
      <h3 className="text-lg font-semibold mb-2">{task.name}</h3>

      <div className="flex justify-between gap-3 text-sm">
        <span>{t("start_date")}:</span>
        <span>{dateFormater(task.start)}</span>
      </div>

      <div className="flex justify-between text-sm">
        <span>{t("end_date")}:</span>
        <span>{dateFormater(task.end)}</span>
      </div>

      <div className="flex justify-between text-sm">
        <span>{t("Duration")}:</span>
        <span>{task.duration} {t("days")}</span>
      </div>

      <div className="flex justify-between items-center text-sm mt-2">
        <span>{t("progress")}:</span>
        <div className="w-full bg-[#78748649] h-2 rounded-lg overflow-hidden ml-3">
          <div
            className="bg-[#535EF9] h-full"
            style={{ width: `${task.progress}%` }}
          />
        </div>
        <span className="ml-2">{Number(task.progress).toFixed()}%</span>
      </div>
    </div>
  );
};
